// src/pages/PartyGuidePage.jsx
import React from "react";

export default function PartyGuidePage() {
  return (
    <div>
      {/* 헤더 */}
      <header className="mb-5 sm:mb-6">
        <h1 className="text-xl/7 font-semibold text-zinc-950 sm:text-xl/8 lg:text-2xl/8 dark:text-white">
          공격대 전투 분석 사용 가이드
        </h1>
        <p className="mt-1 text-sm text-zinc-600 sm:text-base dark:text-zinc-400">
          공격대 페이지를 통해 공대원들과 전투 분석 결과를 공유하고, 트라이별 평균 피해량을 확인할 수 있습니다.
          <br className="hidden sm:block" />
          아래 절차에 따라 <b className="text-zinc-900 dark:text-white">공격대를 생성/참여 → 전투 기록 공유 → 평균 계산</b> 순서로 진행하세요.
        </p>
      </header>

      {/* 섹션 1: 공격대 생성 및 참여 */}
      <section className="rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">
              1
            </span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">공격대 생성 및 참여</h2>
          </div>
        </div>

        <ol className="divide-y divide-zinc-100 dark:divide-white/10">
          {/* 생성 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="1" title="공격대 생성" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              <li><span className="btn btn-primary">공격대 생성</span> 버튼을 클릭합니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">
                  레이드 선택 → 인원수 설정 → 닉네임(인게임 캐릭터 닉네임) 입력 → <span className="btn btn-secondary">확인하기</span> 버튼 클릭
                </b>{" "}
                순서로 진행합니다.
              </li>
              <li>확인 버튼을 누르면 아래에 자동으로 직업/직업각인이 표시됩니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">
                  반드시 직업/직업각인이 올바르게 표시되는지 확인하세요.
                </b>
              </li>
              <li><span className="btn btn-primary">공격대 생성하기</span> 버튼을 클릭하면 공격대가 생성됩니다.</li>
              <li>생성된 공격대 페이지에서 코드를 확인할 수 있으며, 이 코드를 공대원들과 공유하면 참여가 가능합니다.</li>
            </ol>
          </li>

          {/* 참여 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="2" title="공격대 참여" />
            <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              <li><span className="btn">공격대 참여</span> 버튼을 클릭합니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">
                  공대장이 공유한 코드 입력 → 닉네임(인게임 캐릭터 닉네임) 입력 → <span className="btn btn-secondary">확인하기</span> 버튼 클릭
                </b>{" "}
                순서로 진행합니다.
              </li>
              <li>확인 버튼을 누르면 아래에 자동으로 직업/직업각인이 표시됩니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">
                  반드시 직업/직업각인이 올바르게 표시되는지 확인하세요.
                </b>
              </li>
              <li><span className="btn btn-primary">공격대 참여하기</span> 버튼을 클릭하면 해당 공격대 페이지로 이동합니다.</li>
            </ol>

            <Callout intent="rose" className="mt-3">
              생성/참여 시 반드시 <b>인게임 캐릭터 닉네임</b>을 입력하고 <b>[확인]</b>을 눌러
              <b> 직업/직업각인</b>이 올바르게 표시되는지 확인한 후 진행해야 합니다.
            </Callout>

            {/* <div className="mt-3">
              <ImagePlaceholder label="공격대 참여 화면 예시" />
            </div> */}
          </li>

        </ol>
      </section>

      {/* 섹션 2: 기록 방법 */}
      <section className="mt-5 sm:mt-6 rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">
              2
            </span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">기록 방법</h2>
          </div>
        </div>

        <ol className="divide-y divide-zinc-100 dark:divide-white/10">
          {/* 2-1. 창 모드 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="1" title="로스트아크를 창 모드(1920×1080)로 실행" />
            <p className="text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              분석의 정확도를 위해 반드시 <b className="text-zinc-900 dark:text-white">1920×1080</b> 해상도의 창 모드에서 실행해 주세요.
            </p>
            <p className="mt-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              <span className="btn btn-black">화면 공유 시작</span> 버튼을 클릭한 뒤, 창 탭에서
              <b className="text-zinc-900 dark:text-white"> LOST ARK </b>를 선택하고 <b>공유</b> 버튼을 눌러 주세요.
            </p>

            {/* <div className="mt-3">
              <ImagePlaceholder label="화면 공유 예시 캡처 자리" />
            </div> */}
          </li>

          {/* 2-2. 전투 분석기 인식 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="2" title="전투 분석기 인식" />
            <ol className="list-disc pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              <li>전투 분석기(기본 단축키 Alt + .)를 열고 화면 우측 상단에 배치합니다.</li>
              <li>최근 트라이 기록을 불러온 뒤, <span className="btn">전투 분석기 인식</span> 버튼을 클릭합니다.</li>
              <li>
                <b className="text-zinc-900 dark:text-white">워로드, 기공사, 리퍼</b> 직업은 전투 분석기 표가 한 화면에 모두 표시되지 않을 수 있습니다.
                <ul className="mt-1 list-disc pl-5 space-y-1">
                  <li>전투 분석기 하단까지 <b>스크롤</b>한 뒤</li>
                  <li>
                    <span className="btn">스크롤 후 전투 분석기 인식</span> 버튼을 클릭하여 하단 구간까지 정확히 인식해 주세요.
                  </li>
                </ul>
              </li>
              <li>잘못 인식된 경우 <span className="btn btn-danger">전투 분석기 인식 재실행</span> 을 선택합니다.</li>
            </ol>

            {/* <div className="mt-3">
              <ImagePlaceholder label="전투 분석기 캡처 자리" />
            </div> */}
          </li>

          {/* 2-3. 공유 */}
          <li className="px-3 py-4 sm:p-4">
            <StepTitle n="3" title="공유" />
            <ol className="list-disc pl-5 space-y-1.5 sm:space-y-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
              <li>간단한 메모를 작성한 뒤 <span className="btn">공유하기</span> 버튼을 누르면 공대원들과 기록을 공유할 수 있습니다.</li>
              <li>다른 공대원의 공유 기록을 반영하려면 <span className="btn btn-sm btn-primary">새로고침</span> 을 클릭하세요.</li>
            </ol>
          </li>
        </ol>
      </section>

      {/* 섹션 3: 공격대 평균 계산 */}
      <section className="mt-5 sm:mt-6 rounded-lg border border-zinc-950/10 bg-white dark:border-white/10 dark:bg-zinc-900">
        <div className="border-b border-zinc-950/10 px-3 py-3 sm:p-4 dark:border-white/10">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 text-sm font-semibold border border-indigo-200 dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20">
              3
            </span>
            <h2 className="text-sm sm:text-base font-semibold text-zinc-950 dark:text-white">공격대 평균 계산</h2>
          </div>
        </div>

        <div className="px-3 py-4 sm:p-4 text-sm sm:text-base text-zinc-700 space-y-2 dark:text-zinc-300">
          <ol className="list-decimal pl-5 space-y-1.5 sm:space-y-2">
            <li>공대원들이 모두 업로드를 완료하면, <span className="btn btn-sm btn-primary">새로고침</span> 을 눌러 리스트에 모든 기록이 표시되도록 합니다.</li>
            <li>
              <span className="btn btn-sm">공격대 평균 계산</span> 을 클릭하면 평균 값이 자동으로 계산 및 기록됩니다.
            </li>
            <li>
              <span className="btn btn-sm">공격대 평균 확인</span> 을 클릭하면 트라이별 전투 기록 지표를 그래프로 확인할 수 있습니다.
            </li>
          </ol>
        </div>
      </section>
    </div>
  );
}

/* —————— 작은 서브 컴포넌트들 —————— */

function StepTitle({ n, title }) {
  return (
    <div className="mb-2 inline-flex items-center gap-2">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-50 text-zinc-700 text-[11px] sm:text-sm font-semibold border border-zinc-950/10 dark:bg-white/10 dark:text-zinc-300 dark:border-white/10">
        {n}
      </span>
      <h3 className="text-sm sm:text-base font-semibold text-zinc-900 dark:text-white">{title}</h3>
    </div>
  );
}

// function ImagePlaceholder({ label = "이미지 자리" }) {
//   return (
//     <div
//       className="relative w-full overflow-hidden rounded-md border border-dashed border-zinc-950/10 bg-zinc-50 dark:border-white/10 dark:bg-white/5"
//       style={{ minHeight: 140 }}
//     >
//       <div className="absolute inset-0 flex items-center justify-center">
//         <div className="text-sm text-zinc-500 dark:text-zinc-400">{label}</div>
//       </div>
//     </div>
//   );
// }

function Callout({ children, intent = "zinc" }) {
  const tone =
    intent === "rose"
      ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-400/20 dark:text-rose-300 dark:border-rose-300/20"
      : "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-400/20 dark:text-zinc-300 dark:border-zinc-300/20";
  return (
    <div
      className={`mt-2 rounded-md border px-3 py-2 sm:px-3.5 sm:py-2.5 text-[12px] sm:text-sm leading-5 sm:leading-6 ${tone}`}
    >
      {children}
    </div>
  );
}
